// components/SalesPlan.js

import React, { useEffect, useState } from 'react';
import {
    Box,
    Text,
    SimpleGrid,
    List,
    ListItem,
    ListIcon,
    Divider,
    VStack,
    Spinner,
    Alert,
    AlertIcon,
    Image,
    HStack,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { useParams } from 'react-router-dom';
import { getAnchorSubscriptions } from '../services/anchorService';

// Utility function to render text with line breaks
const renderTextWithBreaks = (text) => {
    if (!text) return text;
    
    const parts = text.split('<br>');
    if (parts.length === 1) return text;
    
    return parts.map((part, index) => (
        <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 && <br />}
        </React.Fragment>
    ));
};

const SalesPlan = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { id } = useParams();

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await getAnchorSubscriptions(id);
                
                // Handle both direct array and nested structure
                const items = response?.items || response || [];

                const formattedPlans = items.map(({ subscription }) => {
                    const {
                        name,
                        amount,
                        data
                    } = subscription;

                    return {
                        name: name || 'Unnamed Plan',
                        amount: amount || 0,
                        features: [], // You can populate this if needed
                        benefits: data?.benefits || [],
                        consultations: data?.consultation || [],
                        saveOn: data?.save_on || [],
                    };
                });

                setPlans(formattedPlans);
            } catch (err) {
                console.error('Error fetching plans:', err);
                setError('Failed to load plans');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchPlans();
        }
    }, [id]);

    if (loading) {
        return (
            <VStack spacing={4} mt={10}>
                <Spinner size="xl" />
                <Text>Loading plans...</Text>
            </VStack>
        );
    }

    if (error) {
        return (
            <Alert status="error" mt={6}>
                <AlertIcon />
                {error}
            </Alert>
        );
    }

    if (plans.length === 0) {
        return (
            <Alert status="info" mt={6}>
                <AlertIcon />
                No plans available for this anchor.
            </Alert>
        );
    }

    return (
        <>
            <Text fontWeight="bold" fontSize="20px" my={2}>
                Plans available for sale
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {plans.map((plan, index) => (
                    <Box
                        key={index}
                        bg="white"
                        p={6}
                        borderRadius="md"
                        boxShadow="sm"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ boxShadow: "md" }}
                        transition="box-shadow 0.2s"
                    >
                        <Text fontSize="xl" fontWeight="bold" color="blue.500" mb={2}>
                            {plan.name}
                        </Text>
                        <Text fontSize="2xl" fontWeight="semibold" color="green.600" mb={4}>
                            ₹{plan.amount?.toLocaleString('en-IN')}
                        </Text>

                        {/* Features */}
                        {plan.features?.length > 0 && (
                            <VStack align="start" spacing={3} mb={4}>
                                {plan.features.map(([label, value], idx) => (
                                    <Box key={idx} w="100%">
                                        {typeof value === 'boolean' ? (
                                            value && (
                                                <List spacing={1}>
                                                    <ListItem display="flex" alignItems="center">
                                                        <ListIcon as={CheckCircleIcon} color="green.500" />
                                                        <Text fontSize="sm">{renderTextWithBreaks(label)}</Text>
                                                    </ListItem>
                                                </List>
                                            )
                                        ) : (
                                            <>
                                                <Text fontSize="sm" color="gray.600">
                                                    {renderTextWithBreaks(label)}
                                                </Text>
                                                <Text fontWeight="medium">{renderTextWithBreaks(value)}</Text>
                                                <Divider />
                                            </>
                                        )}
                                    </Box>
                                ))}
                            </VStack>
                        )}

                        {/* Benefits */}
                        {plan.benefits?.length > 0 && (
                            <>
                                <Text fontWeight="semibold" mt={4} mb={3}>
                                    Benefits
                                </Text>
                                <VStack align="start" spacing={3}>
                                    {plan.benefits.map((benefit, idx) => (
                                        <HStack key={idx} spacing={3} align="flex-start" w="100%">
                                            <Image 
                                                src={benefit.url} 
                                                boxSize="24px" 
                                                flexShrink={0}
                                                alt={benefit.key}
                                                fallback={<Box boxSize="24px" bg="gray.200" borderRadius="md" />}
                                            />
                                            <Text fontSize="sm" lineHeight="1.4">
                                                {renderTextWithBreaks(benefit.key)}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </>
                        )}

                        {/* Consultations */}
                        {plan.consultations?.length > 0 && (
                            <>
                                <Text fontWeight="semibold" mt={4} mb={3}>
                                    Consultations
                                </Text>
                                <VStack align="start" spacing={3}>
                                    {plan.consultations.map((consult, idx) => (
                                        <HStack key={idx} spacing={3} align="center" w="100%">
                                            <Image 
                                                src={consult.url} 
                                                boxSize="24px" 
                                                flexShrink={0}
                                                alt={consult.key}
                                                fallback={<Box boxSize="24px" bg="gray.200" borderRadius="md" />}
                                            />
                                            <Text fontSize="sm">
                                                {renderTextWithBreaks(consult.key)}: {consult.value}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </>
                        )}

                        {/* Save On */}
                        {/* {plan.saveOn?.length > 0 && (
                            <>
                                <Text fontWeight="semibold" mt={4} mb={3} >
                                    Save On
                                </Text>
                                <VStack align="start" spacing={3}>
                                    {plan.saveOn.map((save, idx) => (
                                        <HStack key={idx} spacing={3} align="center" w="100%">
                                            <Image 
                                                src={save.url} 
                                                boxSize="24px" 
                                                flexShrink={0}
                                                alt={save.key}
                                                fallback={<Box boxSize="24px" bg="gray.200" borderRadius="md" />}
                                            />
                                            <Text fontSize="sm">
                                                {renderTextWithBreaks(save.key)}: {save.value}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </>
                        )} */}
                    </Box>
                ))}
            </SimpleGrid>
        </>
    );
};

export default SalesPlan;
